import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { api, clearToken, getToken, setToken } from '../api/client';

export interface StaffProfile {
  staff_id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: 'nurse' | 'county_admin' | 'super_admin';
  county_code: string;
  department: string;
  mfa_enabled: boolean;
}

interface AuthState {
  staff: StaffProfile | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (token: string, staff: StaffProfile) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

// SOC 2: 15-minute inactivity auto-logout.
const INACTIVITY_MS = 15 * 60 * 1000;

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [staff, setStaff] = useState<StaffProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef<number | undefined>(undefined);

  const logout = useCallback(() => {
    clearToken();
    setStaff(null);
    if (window.location.pathname !== '/login') {
      window.location.assign('/login');
    }
  }, []);

  const resetInactivityTimer = useCallback(() => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(logout, INACTIVITY_MS);
  }, [logout]);

  const login = useCallback(
    (token: string, profile: StaffProfile) => {
      setToken(token);
      setStaff(profile);
      resetInactivityTimer();
    },
    [resetInactivityTimer]
  );

  // Restore session on first load.
  useEffect(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get('/api/admin/auth/me')
      .then((res) => {
        setStaff(res.data.staff as StaffProfile);
        resetInactivityTimer();
      })
      .catch(() => clearToken())
      .finally(() => setLoading(false));
  }, [resetInactivityTimer]);

  // Track activity for the inactivity timer while authenticated.
  useEffect(() => {
    if (!staff) return;
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    const handler = () => resetInactivityTimer();
    events.forEach((e) => window.addEventListener(e, handler));
    resetInactivityTimer();
    return () => {
      events.forEach((e) => window.removeEventListener(e, handler));
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [staff, resetInactivityTimer]);

  const value = useMemo<AuthState>(
    () => ({
      staff,
      loading,
      isAuthenticated: !!staff,
      login,
      logout,
    }),
    [staff, loading, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
